import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, Search, Building2, MapPin, ShoppingCart, User } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Apartments = () => {
  const [items, setItems] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [filteredStates, setFilteredStates] = useState([]);
  const [filteredCities, setFilteredCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    country_id: '',
    state_id: '',
    city_id: '',
    pin_code: '',
    manager_name: '',
    manager_number: '',
    is_active: true
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchItems();
    fetchCountries();
    fetchAllStates();
    fetchAllCities();
  }, []);

  // Filter states when country changes
  useEffect(() => {
    if (formData.country_id && states.length > 0) {
      const filtered = states.filter(s => s.country_id === formData.country_id);
      setFilteredStates(filtered);
    } else {
      setFilteredStates([]);
    }
  }, [formData.country_id, states]);

  // Filter cities when state changes
  useEffect(() => {
    if (formData.state_id && cities.length > 0) {
      const filtered = cities.filter(c => c.state_id === formData.state_id);
      setFilteredCities(filtered);
    } else {
      setFilteredCities([]);
    }
  }, [formData.state_id, cities]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/apartments`, {
        withCredentials: true
      });
      setItems(response.data);
    } catch (error) {
      console.error('Failed to fetch apartments:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/countries`, {
        withCredentials: true
      });
      setCountries(response.data.filter(c => c.is_active));
    } catch (error) {
      console.error('Failed to fetch countries:', error);
    }
  };

  const fetchAllStates = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/states`, {
        withCredentials: true
      });
      setStates(response.data.filter(s => s.is_active));
    } catch (error) {
      console.error('Failed to fetch states:', error);
    }
  };

  const fetchAllCities = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/cities`, {
        withCredentials: true
      });
      setCities(response.data.filter(c => c.is_active));
    } catch (error) {
      console.error('Failed to fetch cities:', error);
    }
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        name: item.name || '',
        address: item.address || '',
        country_id: item.country_id || '',
        state_id: item.state_id || '',
        city_id: item.city_id || '',
        pin_code: item.pin_code || '',
        manager_name: item.manager_name || '',
        manager_number: item.manager_number || '',
        is_active: item.is_active
      });
    } else {
      setEditingItem(null);
      setFormData({
        name: '',
        address: '',
        country_id: '',
        state_id: '',
        city_id: '',
        pin_code: '',
        manager_name: '',
        manager_number: '',
        is_active: true
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        name: formData.name,
        address: formData.address || null,
        country_id: formData.country_id,
        state_id: formData.state_id,
        city_id: formData.city_id,
        pin_code: formData.pin_code || null,
        manager_name: formData.manager_name || null,
        manager_number: formData.manager_number || null,
        is_active: formData.is_active
      };

      if (editingItem) {
        await axios.put(
          `${API_URL}/api/admin/apartments/${editingItem.id}`,
          payload,
          { withCredentials: true }
        );
      } else {
        await axios.post(
          `${API_URL}/api/admin/apartments`,
          payload,
          { withCredentials: true }
        );
      }
      setDialogOpen(false);
      fetchItems();
    } catch (error) {
      console.error('Failed to save:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this apartment?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/apartments/${id}`, {
        withCredentials: true
      });
      fetchItems();
    } catch (error) {
      console.error('Failed to delete:', error);
    }
  };

  const handleCountryChange = (value) => {
    setFormData({ ...formData, country_id: value, state_id: '', city_id: '' });
  };

  const handleStateChange = (value) => {
    setFormData({ ...formData, state_id: value, city_id: '' });
  };

  const filteredItems = items.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.address && item.address.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (item.manager_name && item.manager_name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const canSave = () => {
    return formData.name && formData.country_id && formData.state_id && formData.city_id;
  };

  return (
    <div data-testid="apartments-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold text-text-primary mb-1">Apartments</h1>
          <p className="font-body text-text-secondary">Manage apartment societies</p>
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-xl gap-2"
          data-testid="add-apartment-btn"
        >
          <Plus size={18} />
          Add Apartment
        </Button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-2xl p-4 border border-surface-border mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" size={18} />
          <Input
            placeholder="Search apartments..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 rounded-xl border-surface-border"
          />
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="bg-white rounded-2xl border border-surface-border flex flex-col items-center justify-center h-48 text-text-muted">
          <Building2 size={48} className="mb-4 opacity-50" />
          <p className="font-body">No apartments found</p>
          <Button variant="link" onClick={() => handleOpenDialog()} className="text-brand-primary">
            Add your first apartment
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredItems.map((item) => (
            <div key={item.id} className={`bg-white rounded-2xl border border-surface-border overflow-hidden flex flex-col group hover:shadow-md transition-shadow ${!item.is_active ? 'opacity-60' : ''}`} data-testid={`apartment-card-${item.id}`}>
              {/* Header */}
              <div className="p-4 flex-1">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
                    <Building2 size={20} className="text-blue-600" />
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => handleOpenDialog(item)} className="p-1.5 bg-gray-50 rounded-lg text-text-secondary hover:text-brand-primary" title="Edit">
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => handleDelete(item.id)} className="p-1.5 bg-gray-50 rounded-lg text-text-secondary hover:text-red-600" title="Delete">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                <h3 className="font-body font-semibold text-text-primary mb-1 line-clamp-1">{item.name}</h3>
                {item.address && <p className="font-body text-sm text-text-muted line-clamp-1 mb-2">{item.address}</p>}
                <div className="flex items-center gap-1 text-xs text-text-secondary font-body mb-2">
                  <MapPin size={12} />
                  <span className="line-clamp-1">{[item.city_name, item.state_name].filter(Boolean).join(', ') || '-'}{item.pin_code ? ` - ${item.pin_code}` : ''}</span>
                </div>
                {item.manager_name && (
                  <div className="flex items-center gap-1 text-xs text-text-secondary font-body">
                    <User size={12} />
                    <span>{item.manager_name}</span>
                  </div>
                )}
                <span className={`inline-block mt-3 px-2 py-0.5 rounded-full text-xs font-medium ${item.is_active ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                  {item.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              {/* Orders this month */}
              <div className="border-t border-surface-border bg-brand-primary/5 px-4 py-2.5 flex items-center gap-2">
                <ShoppingCart size={14} className="text-brand-primary" />
                <span className="font-body text-sm font-semibold text-brand-primary">
                  {item.orders_this_month || 0} order{(item.orders_this_month || 0) !== 1 ? 's' : ''} this month
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingItem ? 'Edit' : 'Add'} Apartment
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="name" className="font-body font-medium">Name <span className="text-red-500">*</span></Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter apartment name"
                className="rounded-xl"
              />
            </div>

            {/* Address */}
            <div className="space-y-2">
              <Label htmlFor="address" className="font-body font-medium">Address</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Enter full address"
                className="rounded-xl"
              />
            </div>

            {/* Country */}
            <div className="space-y-2">
              <Label className="font-body font-medium">Country <span className="text-red-500">*</span></Label>
              <Select
                value={formData.country_id}
                onValueChange={handleCountryChange}
              >
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Select a country" />
                </SelectTrigger>
                <SelectContent>
                  {countries.map(country => (
                    <SelectItem key={country.id} value={country.id}>
                      {country.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {countries.length === 0 && (
                <p className="text-xs text-amber-600">No countries found. Please add countries first.</p>
              )}
            </div>

            {/* State */}
            <div className="space-y-2">
              <Label className="font-body font-medium">State <span className="text-red-500">*</span></Label>
              <Select
                value={formData.state_id}
                onValueChange={handleStateChange}
                disabled={!formData.country_id}
              >
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder={formData.country_id ? "Select a state" : "Select country first"} />
                </SelectTrigger>
                <SelectContent>
                  {filteredStates.map(state => (
                    <SelectItem key={state.id} value={state.id}>
                      {state.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.country_id && filteredStates.length === 0 && (
                <p className="text-xs text-amber-600">No states found for this country.</p>
              )}
            </div>

            {/* City */}
            <div className="space-y-2">
              <Label className="font-body font-medium">City <span className="text-red-500">*</span></Label>
              <Select
                value={formData.city_id}
                onValueChange={(value) => setFormData({ ...formData, city_id: value })}
                disabled={!formData.state_id}
              >
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder={formData.state_id ? "Select a city" : "Select state first"} />
                </SelectTrigger>
                <SelectContent>
                  {filteredCities.map(city => (
                    <SelectItem key={city.id} value={city.id}>
                      {city.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.state_id && filteredCities.length === 0 && (
                <p className="text-xs text-amber-600">No cities found for this state.</p>
              )}
            </div>

            {/* Pin Code */}
            <div className="space-y-2">
              <Label htmlFor="pin_code" className="font-body font-medium">Pin Code</Label>
              <Input
                id="pin_code"
                value={formData.pin_code}
                onChange={(e) => setFormData({ ...formData, pin_code: e.target.value })}
                placeholder="Enter pin code"
                className="rounded-xl"
              />
            </div>

            <hr className="border-surface-border" />

            {/* Manager Name */}
            <div className="space-y-2">
              <Label htmlFor="manager_name" className="font-body font-medium">Manager Name</Label>
              <Input
                id="manager_name"
                value={formData.manager_name}
                onChange={(e) => setFormData({ ...formData, manager_name: e.target.value })}
                placeholder="Enter manager name"
                className="rounded-xl"
              />
            </div>

            {/* Manager Number */}
            <div className="space-y-2">
              <Label htmlFor="manager_number" className="font-body font-medium">Manager Number</Label>
              <Input
                id="manager_number"
                value={formData.manager_number}
                onChange={(e) => setFormData({ ...formData, manager_number: e.target.value })}
                placeholder="Enter manager phone number"
                className="rounded-xl"
              />
            </div>

            {/* Active */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="rounded border-surface-border"
              />
              <Label htmlFor="is_active" className="font-body">Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-xl">
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || !canSave()}
              className="bg-brand-primary hover:bg-brand-primary-hover rounded-xl"
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Apartments;
